/**
 * Liquid Glass : lentille de bord et lumière au pointeur.
 *
 * Réfraction : une carte de déplacement SVG par taille de verre, branchée en
 * fin de `backdrop-filter` par la variable `--lg-lentille`. Chromium
 * seulement : Safari accepte `url()` dans `backdrop-filter` mais n'affiche
 * alors plus rien, Firefox l'ignore. On teste donc le moteur, pas
 * `CSS.supports`. Ailleurs, ou en transparence réduite, le verre garde son
 * matériau et `liquid-glass.css` renforce la lueur du bord.
 *
 * Lumière : sur une commande de verre, `--lg-x` / `--lg-y` suivent le pointeur
 * pour que l'éclat d'appui naisse sous le doigt.
 *
 * Présentation seulement : aucune donnée, aucune route.
 */

/** Les verres flottants qui reçoivent la lentille (mêmes sélecteurs que le CSS). */
const VERRES = [
  "[data-ui='regie-shell'] > div > [data-ui='sidebar']",
  "[role='dialog']:has(> [data-ui='sidebar'])",
  "[data-ui='topbar'] button:not([role='checkbox'], [role='switch'], [role='combobox'])",
  "[data-ui='page-heading'] button:not([role='checkbox'], [role='switch'], [role='combobox'])",
  "[data-ui='main'] [role='tablist']",
  "[data-ui='main'] [role='group']:has(> button[aria-pressed])",
  "[role='menu']",
  "[role='listbox']",
].join(',');
/** Les commandes dont l'éclat suit le pointeur. */
const LUMIERE = [
  "[data-ui='topbar'] button",
  "[data-ui='page-heading'] button",
  "[data-ui='nav-link']",
  "[role='tab']",
  "[data-ui='main'] button",
  "[data-ui='dashboard-metric']",
].join(',');
const MAX_FILTRES = 64;
const SURFACE_MAX = 900_000;

function estChromium(): boolean {
  const ua = (navigator as Navigator & { userAgentData?: { brands: { brand: string }[] } })
    .userAgentData;
  return !!ua?.brands.some((b) => b.brand === 'Chromium');
}

/** Distance signée au bord d'un rectangle arrondi et normale sortante. */
function bord(px: number, py: number, hw: number, hh: number, r: number) {
  const qx = Math.abs(px) - hw;
  const qy = Math.abs(py) - hh;
  const ox = Math.max(qx, 0);
  const oy = Math.max(qy, 0);
  let nx: number;
  let ny: number;
  if (qx > 0 || qy > 0) {
    nx = Math.sign(px) * ox;
    ny = Math.sign(py) * oy;
  } else if (qx > qy) {
    nx = Math.sign(px);
    ny = 0;
  } else {
    nx = 0;
    ny = Math.sign(py);
  }
  const l = Math.hypot(nx, ny) || 1;
  return { d: -(Math.hypot(ox, oy) + Math.min(Math.max(qx, qy), 0) - r), nx: nx / l, ny: ny / l };
}

/**
 * Lentille convexe : décalage A·(1−u)^2,2 sur une bande de bord (u = 0 au bord),
 * nul au centre, échantillonné vers l'intérieur. La pente reste < 1 : le fond
 * est grossi près du bord sans jamais se replier (pas d'image fantôme).
 * Rouge et vert portent le décalage, bleu la force de la bande (l'éclat du bord).
 */
function carte(w: number, h: number, r: number, bande: number): string {
  const toile = document.createElement('canvas');
  toile.width = w;
  toile.height = h;
  const ctx = toile.getContext('2d');
  if (!ctx) return '';
  const image = ctx.createImageData(w, h);
  const d = image.data;
  const hw = w / 2 - r;
  const hh = h / 2 - r;
  for (let j = 0; j < h; j++) {
    for (let i = 0; i < w; i++) {
      const s = bord(i + 0.5 - w / 2, j + 0.5 - h / 2, hw, hh, r);
      const m = s.d >= 0 && s.d < bande ? -Math.pow(1 - s.d / bande, 2.2) : 0;
      const k = (j * w + i) * 4;
      d[k] = 128 + s.nx * m * 127;
      d[k + 1] = 128 + s.ny * m * 127;
      d[k + 2] = -m * 255;
      d[k + 3] = 255;
    }
  }
  ctx.putImageData(image, 0, 0);
  return toile.toDataURL();
}

function creerLentille() {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('width', '0');
  svg.setAttribute('height', '0');
  svg.style.position = 'absolute';
  svg.style.pointerEvents = 'none';
  const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
  svg.append(defs);
  document.body.append(svg);

  const filtres = new Map<string, string>();
  const verres = new Set<HTMLElement>();
  let attente = 0;

  function filtrePour(el: HTMLElement): string | null {
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    if (w < 8 || h < 8 || w * h > SURFACE_MAX) return null;
    const rayon = Number.parseFloat(getComputedStyle(el).borderTopLeftRadius) || 0;
    const r = Math.min(rayon, w / 2, h / 2);
    const cle = `${w}x${h}r${Math.round(r)}`;
    const connu = filtres.get(cle);
    if (connu) return connu;
    if (filtres.size >= MAX_FILTRES) {
      filtres.clear();
      defs.replaceChildren();
    }
    const bande = Math.min(h / 2, w / 2, 28);
    const id = `lg-lentille-${cle}`;
    const f = document.createElementNS('http://www.w3.org/2000/svg', 'filter');
    f.id = id;
    for (const [nom, valeur] of [
      ['x', '0'],
      ['y', '0'],
      ['width', String(w)],
      ['height', String(h)],
      ['filterUnits', 'userSpaceOnUse'],
      ['color-interpolation-filters', 'sRGB'],
    ]) {
      f.setAttribute(nom as string, valeur as string);
    }
    // Le fond déplacé, puis la même image, plus vive, dans la bande de bord : l'épaisseur du verre.
    f.innerHTML = `<feImage href="${carte(w, h, r, bande)}" width="${w}" height="${h}" preserveAspectRatio="none" result="m"/><feDisplacementMap in="SourceGraphic" in2="m" scale="${(0.9 * bande).toFixed(1)}" xChannelSelector="R" yChannelSelector="G" result="d"/><feColorMatrix in="m" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.9 0 0" result="b"/><feColorMatrix in="d" type="matrix" values="1.18 0 0 0 0.05 0 1.18 0 0 0.05 0 0 1.18 0 0.05 0 0 0 1 0" result="v"/><feComposite in="v" in2="b" operator="in" result="e"/><feMerge><feMergeNode in="d"/><feMergeNode in="e"/></feMerge>`;
    defs.append(f);
    filtres.set(cle, id);
    return id;
  }

  function appliquer() {
    attente = 0;
    for (const el of document.querySelectorAll<HTMLElement>(VERRES)) {
      if (!verres.has(el)) {
        verres.add(el);
        taille.observe(el);
      }
    }
    for (const el of verres) {
      if (!el.isConnected) {
        verres.delete(el);
        taille.unobserve(el);
        continue;
      }
      const id = filtrePour(el);
      if (id) el.style.setProperty('--lg-lentille', `url(#${id})`);
      else el.style.removeProperty('--lg-lentille');
    }
  }
  const planifier = () => {
    if (!attente) attente = requestAnimationFrame(appliquer);
  };
  const taille = new ResizeObserver(planifier);
  const arbre = new MutationObserver(planifier);
  arbre.observe(document.body, { childList: true, subtree: true });
  planifier();

  return () => {
    cancelAnimationFrame(attente);
    arbre.disconnect();
    taille.disconnect();
    for (const el of verres) el.style.removeProperty('--lg-lentille');
    svg.remove();
  };
}

function suivreLumiere() {
  let attente = 0;
  let dernier: PointerEvent | null = null;
  const poser = () => {
    attente = 0;
    const e = dernier;
    const cible = e?.target instanceof Element ? e.target.closest<HTMLElement>(LUMIERE) : null;
    if (!e || !cible) return;
    const r = cible.getBoundingClientRect();
    cible.style.setProperty('--lg-x', `${Math.round(e.clientX - r.left)}px`);
    cible.style.setProperty('--lg-y', `${Math.round(e.clientY - r.top)}px`);
  };
  const suivre = (e: PointerEvent) => {
    dernier = e;
    if (!attente) attente = requestAnimationFrame(poser);
  };
  document.addEventListener('pointermove', suivre, { passive: true });
  document.addEventListener('pointerdown', suivre, { passive: true });
  return () => {
    cancelAnimationFrame(attente);
    document.removeEventListener('pointermove', suivre);
    document.removeEventListener('pointerdown', suivre);
  };
}

/** Branche la lentille et la lumière tant que Liquid Glass est le thème appliqué. */
export function demarrerLiquidGlass(): void {
  if (typeof document === 'undefined' || typeof ResizeObserver === 'undefined') return;
  const racine = document.documentElement;
  const sobre = window.matchMedia(
    '(prefers-reduced-transparency: reduce), (prefers-contrast: more)',
  );
  const chromium = estChromium();
  let arreterLentille: (() => void) | null = null;
  let arreterLumiere: (() => void) | null = null;

  const accorder = () => {
    const actif = racine.dataset.design === 'liquid-glass';
    const lentille = actif && chromium && !sobre.matches;
    if (lentille && !arreterLentille) {
      arreterLentille = creerLentille();
      racine.dataset.lgLentille = '';
    } else if (!lentille && arreterLentille) {
      arreterLentille();
      arreterLentille = null;
      delete racine.dataset.lgLentille;
    }
    if (actif && !arreterLumiere) arreterLumiere = suivreLumiere();
    else if (!actif && arreterLumiere) {
      arreterLumiere();
      arreterLumiere = null;
    }
  };
  new MutationObserver(accorder).observe(racine, { attributeFilter: ['data-design'] });
  sobre.addEventListener('change', accorder);
  accorder();
}
